import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "@clerk/clerk-react";
import { setApiTokenGetter } from "@web/lib/api.ts";

/**
 * Wires Clerk session tokens into `api()` so every TanStack Query / mutation
 * call sends `Authorization: Bearer …`. Must render under a signed-in tree.
 *
 * Children stay unmounted until the getter is armed — otherwise the first
 * wave of queries races `useEffect` and hit the API with no Bearer → 401.
 *
 * Important: do **not** null the getter (or flip `ready` false) when Clerk
 * recreates `getToken` or Vite HMR remounts — cleanup-then-setup leaves a
 * window where remounted queries fetch with no Authorization.
 */
export function ClerkTokenBridge({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { getToken, isLoaded } = useAuth();
  const [ready, setReady] = useState(false);

  // Keep the module-level getter pointed at the latest getToken. Never clear
  // it here — only on real unmount of this bridge.
  useEffect(() => {
    if (!isLoaded) return;
    setApiTokenGetter(() => getToken());
  }, [getToken, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;

    let cancelled = false;
    void (async () => {
      try {
        await getToken();
      } catch {
        // Still mark ready — subsequent calls surface a clear auth error.
      }
      if (!cancelled) setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [getToken, isLoaded]);

  useEffect(() => {
    return () => {
      setApiTokenGetter(null);
    };
  }, []);

  if (!ready) {
    return (
      fallback ?? (
        <div className="flex h-full items-center justify-center bg-bg text-sm text-muted">
          Loading session…
        </div>
      )
    );
  }

  return children;
}
